import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, Sparkle, XCircle, Storefront } from "@phosphor-icons/react";
import { checkoutStatus } from "../lib/api";

export function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", payment_status: null, order_id: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing" });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const r = await checkoutStatus(sessionId);
        if (cancelled) return;
        setState(r);
        if (r.payment_status === "paid") return;
        if (r.status === "expired" || attempts.current >= 10) return;
        setTimeout(poll, 2000);
      } catch {
        if (attempts.current < 10) setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const failed = state.status === "expired" || state.payment_status === "unpaid" && attempts.current >= 10;

  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <div className={`nb-card ${paid ? "bg-[#B4F0A7]" : failed ? "bg-[#FFA0A0]" : "bg-[#D4C4FB]"}`}>
        <div className="flex items-center gap-3">
          {paid ? (
            <CheckCircle weight="duotone" size={36} />
          ) : failed ? (
            <XCircle weight="duotone" size={36} />
          ) : (
            <Sparkle weight="duotone" size={36} className="animate-spin" />
          )}
          <div>
            <span className="nb-label">{paid ? "Payment confirmed" : failed ? "Payment incomplete" : "Confirming payment"}</span>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter">
              {paid ? "Order placed! 🎉" : failed ? "Something went wrong" : "Hang tight…"}
            </h1>
          </div>
        </div>
        <p className="mt-4 text-base font-medium">
          {paid && "Your order is confirmed and the seller has been notified. Stock has been updated."}
          {failed && "We couldn't confirm your payment. If money was taken, it will be refunded automatically."}
          {!paid && !failed && "We're confirming your payment with Stripe."}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {paid ? (
            <>
              <button data-testid="go-orders" onClick={() => navigate("/orders")} className="nb-btn inline-flex items-center gap-2">
                View my orders
              </button>
              <Link to="/" className="nb-btn nb-btn-ghost inline-flex items-center gap-2">
                <Storefront weight="bold" size={18} /> Keep shopping
              </Link>
            </>
          ) : (
            <Link to="/" className="nb-btn inline-flex items-center gap-2">
              <Storefront weight="bold" size={18} /> Back to marketplace
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export function CheckoutCancel() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-16">
      <div className="nb-card bg-[#FFA0A0]">
        <div className="flex items-center gap-3">
          <XCircle weight="duotone" size={36} />
          <div>
            <span className="nb-label">Payment cancelled</span>
            <h1 className="mt-1 font-display text-3xl font-black tracking-tighter">No charge.</h1>
          </div>
        </div>
        <p className="mt-4 text-base font-medium">Your basket is safe — head back and try again when you're ready.</p>
        <Link to="/" className="nb-btn mt-6 inline-flex items-center gap-2">
          <Storefront weight="bold" size={18} /> Back to marketplace
        </Link>
      </div>
    </div>
  );
}
